import type { RowEditorConfig } from "@dl/web-core-js/table";
import { toast } from "@dl/web-core-js/ui";

interface CrudClient<T> {
  create(entity: T): Promise<T>;
  update(entity: T): Promise<void>;
  remove(id: number): Promise<void>;
}

export interface CrudEditorOptions<T extends { id: number }> {
  client: CrudClient<T>;
  /** Invalidates the page's query so the grid shows what the server stored. */
  refresh: () => Promise<unknown> | unknown;
  t: (key: string, fallback: string) => string;
  fields: RowEditorConfig<T>["fields"];
  /** Omit to make the grid edit-only, as the fixed-row screens are. */
  newRow?: () => T;
  /** Adjusts a row on its way into the edit draft — see `RowEditorConfig`. */
  toDraft?: RowEditorConfig<T>["toDraft"];
  validate?: RowEditorConfig<T>["validate"];
  canDelete?: boolean;
  deleteTitle?: string;
  rowActions?: RowEditorConfig<T>["rowActions"];
  addLabel?: string;
  /** Runs instead of `client.create`/`update` when a screen saves differently. */
  onSave?: (row: T, isNew: boolean) => Promise<void>;
}

/**
 * The `EditorGrid` save/delete lifecycle, written once.
 *
 * Every config grid in the Vaadin app did the same three things on save —
 * call the client, refresh the list, toast — and differed only in its fields
 * and validators. Those are the arguments here; nothing else varies.
 */
export function crudEditor<T extends { id: number }>({
  client,
  refresh,
  t,
  fields,
  newRow,
  toDraft,
  validate,
  canDelete = true,
  deleteTitle,
  rowActions,
  addLabel,
  onSave,
}: CrudEditorOptions<T>): RowEditorConfig<T> {
  return {
    fields,
    ...(newRow ? { newRow } : {}),
    ...(toDraft ? { toDraft } : {}),
    ...(validate ? { validate } : {}),
    ...(rowActions ? { rowActions } : {}),
    ...(addLabel ? { addLabel } : {}),
    onSave: async (row, isNew) => {
      if (onSave) {
        await onSave(row, isNew);
      } else if (isNew) {
        await client.create(row);
      } else {
        await client.update(row);
      }
      await refresh();
      toast.success(t("notification.save.success", "Item saved successfully"));
    },
    ...(canDelete
      ? {
          onDelete: async (row: T) => {
            await client.remove(row.id);
            await refresh();
            toast.success(t("notification.delete.success", "Items deleted successfully"));
          },
          deleteConfirm: () => ({
            title: deleteTitle ?? t("dialog.confirm.delete.title", "Confirm delete"),
            message: t(
              "dialog.confirm.delete.text",
              "Are you sure you want to delete the selected items?",
            ),
          }),
        }
      : {}),
  };
}

/** Text field helper: reads a string property, writes it back trimmed of nothing. */
export function textField<T>(
  get: (row: T) => string | null | undefined,
  set: (row: T, value: string) => T,
  required = false,
): RowEditorConfig<T>["fields"][string] {
  return { kind: "text", get: (row) => get(row) ?? "", set, required };
}

export function numberField<T>(
  get: (row: T) => number | null | undefined,
  set: (row: T, value: string) => T,
  required = false,
): RowEditorConfig<T>["fields"][string] {
  return { kind: "number", get: (row) => get(row) ?? "", set, required };
}

export function selectField<T>(
  get: (row: T) => string | null | undefined,
  set: (row: T, value: string) => T,
  options:
    | readonly string[]
    | Array<{ value: string; label: string }>
    | ((row: T) => Array<{ value: string; label: string }>),
  required = true,
): RowEditorConfig<T>["fields"][string] {
  return {
    kind: "select",
    get,
    set,
    required,
    options:
      typeof options === "function"
        ? options
        : options.map((option) =>
            typeof option === "string" ? { value: option, label: option } : option,
          ),
  };
}

export function checkboxField<T>(
  get: (row: T) => boolean,
  set: (row: T, value: boolean) => T,
): RowEditorConfig<T>["fields"][string] {
  return { kind: "checkbox", get, set };
}

/** `{ ...row, [key]: value }` with the nesting some DTOs need. */
export function patch<T>(row: T, changes: Partial<T>): T {
  return { ...row, ...changes };
}

export function multiSelectField<T>(
  get: (row: T) => string[],
  set: (row: T, value: string[]) => T,
  options: Array<{ value: string; label: string }>,
): RowEditorConfig<T>["fields"][string] {
  return { kind: "multiselect", get, set, options };
}
